import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ConfigService } from './config.service';
import { AuthService } from './auth.service';
import { 
  GetActiveVoucherRequest, GetActiveVoucherResponse, GetCourierCarRequest, GetCourierCarResponse, 
  GetCourierCarListRequest, GetCourierCarListResponse, GetUsersDefaultClientRequest, GetUsersDefaultClientResponse, 
  GetClientListRequest, GetClientListResponse, GetOdometerValueRequest, GetOdometerValueResponse, GetRoutesRequest, 
  GetRoutesResponse, GetRulesRequest, GetRulesResponse, PostEndVoucherRequest, PostEndVoucherResponse, PostAlcoRequest, 
  PostAlcoResponse, PostOdometrStartValueRequest, PostOdometrStartValueResponse, GetAdBlueListRequest, GetAdBlueListResponse, 
  PostAdBlueFuelRequest, PostAdBlueFuelResponse, GetFuelListRequest, GetFuelListResponse, PostMainFuelRequest, PostMainFuelResponse, 
  GetCommentRequest, GetCommentResponse, PostCommentRequest, PostCommentResponse, GetRegionRequest, GetRegionResponse, 
  GetStopsRequest, GetStopsResponse, GetAlcoValueRequest, GetAlcoValueResponse, PostStopsRequest, PostStopsResponse, 
  PostWavesRequest, PostWavesResponse, PostCarWashRequest, PostCarWashResponse, GetCarWashListRequest, GetCarWashListResponse, 
  GetCarWashCompanyNamesListRequest, GetCarWashCompanyNamesListResponse, GetCarAndLoaderRequest, GetCarAndLoaderResponse,
  GetTrailerListResponse,
  GetTrailerListRequest
} from '../models/courier.models';

@Injectable({
  providedIn: 'root'
})
export class CourierService {
  private apiUrl: string;

  constructor(private http: HttpClient, private configService: ConfigService, private authService: AuthService) {
    this.apiUrl = this.configService.getConfig().apiBaseUrl;
  }

  private getHeaders(): HttpHeaders {
    const token = this.authService.getToken();
    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  }

  private getQueryParams(params: any): HttpParams {
    let httpParams = new HttpParams();
    Object.keys(params).forEach(key => {
      httpParams = httpParams.append(key, params[key]);
    });
    return httpParams;
  }

  getActiveVouchers(request: GetActiveVoucherRequest): Observable<GetActiveVoucherResponse> {
    return this.http.get<GetActiveVoucherResponse>(`${this.apiUrl}/CourierApp/GetActiveVoucher`, { 
      headers: this.getHeaders(), 
      params: this.getQueryParams(request) 
    });
  }

  getCar(request: GetCourierCarRequest): Observable<GetCourierCarResponse> {
    return this.http.get<GetCourierCarResponse>(`${this.apiUrl}/CourierApp/GetCar`, { headers: this.getHeaders() });
  }

  getCarList(request: GetCourierCarListRequest): Observable<GetCourierCarListResponse> {
    return this.http.get<GetCourierCarListResponse>(`${this.apiUrl}/CourierApp/GetCarList`, { 
      headers: this.getHeaders(), 
      params: this.getQueryParams(request) 
    });
  }

  getUsersDefaultClient(request: GetUsersDefaultClientRequest): Observable<GetUsersDefaultClientResponse> {
    return this.http.get<GetUsersDefaultClientResponse>(`${this.apiUrl}/CourierApp/GetUsersDefaultClient`, { 
      headers: this.getHeaders(), 
      params: this.getQueryParams(request) 
    });
  }

  getClientList(request: GetClientListRequest): Observable<GetClientListResponse> {
    return this.http.get<GetClientListResponse>(`${this.apiUrl}/CourierApp/GetClientList`, { 
      headers: this.getHeaders(), 
      params: this.getQueryParams(request) 
    });
  }

  getOdometrValue(request: GetOdometerValueRequest): Observable<GetOdometerValueResponse> {
    return this.http.get<GetOdometerValueResponse>(`${this.apiUrl}/CourierApp/GetOdometrValue`, { 
      headers: this.getHeaders(), 
      params: this.getQueryParams(request) 
    });
  }

  getRoute(request: GetRoutesRequest): Observable<GetRoutesResponse> {
    return this.http.get<GetRoutesResponse>(`${this.apiUrl}/CourierApp/GetRoute`, { 
      headers: this.getHeaders(), 
      params: this.getQueryParams(request) 
    });
  }

  getRules(request: GetRulesRequest): Observable<GetRulesResponse> {
    return this.http.get<GetRulesResponse>(`${this.apiUrl}/CourierApp/GetRules`, { 
      headers: this.getHeaders(), 
      params: this.getQueryParams(request) 
    });
  }

  postEnd(request: PostEndVoucherRequest): Observable<PostEndVoucherResponse> {
    return this.http.post<PostEndVoucherResponse>(`${this.apiUrl}/CourierApp/PostEnd`, request, { headers: this.getHeaders() });
  }

  postAlco(request: PostAlcoRequest): Observable<PostAlcoResponse> {
    return this.http.post<PostAlcoResponse>(`${this.apiUrl}/CourierApp/PostAlco`, request, { headers: this.getHeaders() });
  }

  postStart(request: PostOdometrStartValueRequest): Observable<PostOdometrStartValueResponse> {
    return this.http.post<PostOdometrStartValueResponse>(`${this.apiUrl}/CourierApp/PostStart`, request, { headers: this.getHeaders() });
  }

  getAdBlueList(request: GetAdBlueListRequest): Observable<GetAdBlueListResponse> {
    return this.http.get<GetAdBlueListResponse>(`${this.apiUrl}/CourierApp/GetAdBlueList`, { 
      headers: this.getHeaders(), 
      params: this.getQueryParams(request) 
    });
  }

  postAdBlueFuel(request: PostAdBlueFuelRequest): Observable<PostAdBlueFuelResponse> {
    return this.http.post<PostAdBlueFuelResponse>(`${this.apiUrl}/CourierApp/PostAdBlueFuel`, request, { headers: this.getHeaders() });
  }

  getFuelList(request: GetFuelListRequest): Observable<GetFuelListResponse> {
    return this.http.get<GetFuelListResponse>(`${this.apiUrl}/CourierApp/GetFuelList`, { 
      headers: this.getHeaders(), 
      params: this.getQueryParams(request) 
    });
  }

  postMainFuel(request: PostMainFuelRequest): Observable<PostMainFuelResponse> {
    return this.http.post<PostMainFuelResponse>(`${this.apiUrl}/CourierApp/PostFuel`, request, { headers: this.getHeaders() });
  }

  getComment(request: GetCommentRequest): Observable<GetCommentResponse> {
    return this.http.get<GetCommentResponse>(`${this.apiUrl}/CourierApp/GetComment`, { 
      headers: this.getHeaders(), 
      params: this.getQueryParams(request) 
    });
  }

  postComment(request: PostCommentRequest): Observable<PostCommentResponse> {
    return this.http.post<PostCommentResponse>(`${this.apiUrl}/CourierApp/PostComment`, request, { headers: this.getHeaders() });
  }

  getRegionValue(request: GetRegionRequest): Observable<GetRegionResponse> {
    return this.http.get<GetRegionResponse>(`${this.apiUrl}/CourierApp/GetRegionList`, { 
      headers: this.getHeaders(), 
      params: this.getQueryParams(request) 
    });
  }

  getStops(request: GetStopsRequest): Observable<GetStopsResponse> {
    return this.http.get<GetStopsResponse>(`${this.apiUrl}/CourierApp/GetStops`, { 
      headers: this.getHeaders(), 
      params: this.getQueryParams(request) 
    });
  }

  getAlcoValue(request: GetAlcoValueRequest): Observable<GetAlcoValueResponse> {
    return this.http.get<GetAlcoValueResponse>(`${this.apiUrl}/CourierApp/GetAlcoValue`, { 
      headers: this.getHeaders(), 
      params: this.getQueryParams(request) 
    });
  }

  postStops(request: PostStopsRequest): Observable<PostStopsResponse> {
    return this.http.post<PostStopsResponse>(`${this.apiUrl}/CourierApp/PostStops`, request, { headers: this.getHeaders() });
  }

  postWaves(request: PostWavesRequest): Observable<PostWavesResponse> {
    return this.http.post<PostWavesResponse>(`${this.apiUrl}/CourierApp/PostWaves`, request, { headers: this.getHeaders() });
  }

  postCarWash(request: PostCarWashRequest): Observable<PostCarWashResponse> {
    return this.http.post<PostCarWashResponse>(`${this.apiUrl}/CourierApp/PostCarWash`, request, { headers: this.getHeaders() });
  }

  getCarWashList(request: GetCarWashListRequest): Observable<GetCarWashListResponse> {
    return this.http.get<GetCarWashListResponse>(`${this.apiUrl}/CourierApp/GetCarWashList`, { 
      headers: this.getHeaders(), 
      params: this.getQueryParams(request) 
    });
  }

  getCarWashCompanyNamesList(request: GetCarWashCompanyNamesListRequest): Observable<GetCarWashCompanyNamesListResponse> {
    return this.http.get<GetCarWashCompanyNamesListResponse>(`${this.apiUrl}/CourierApp/GetCarWashCompanyNamesList`, { 
      headers: this.getHeaders(), 
      params: this.getQueryParams(request) 
    });
  }

  getCarAndLoader(request: GetCarAndLoaderRequest): Observable<GetCarAndLoaderResponse> {
    return this.http.get<GetCarAndLoaderResponse>(`${this.apiUrl}/CourierApp/GetCarAndLoader`, { 
      headers: this.getHeaders(), 
      params: this.getQueryParams(request) 
    });
  }

  getTrailerList(request: GetTrailerListRequest): Observable<GetTrailerListResponse> {
    return this.http.get<GetTrailerListResponse>(`${this.apiUrl}/CourierApp/get-trailer-list`, { 
      headers: this.getHeaders(), 
      params: this.getQueryParams(request) 
    });
  }
}